/**
 * GrantsSection — "Your budget isn't the blocker" landing section.
 *
 * Three federal program cards + insurance angle + grant-help CTA.
 * All facts sourced and current; deadlines carry verify-cycle microcopy.
 */

import { Link } from 'react-router-dom';

const PROGRAMS = [
  {
    name: 'Safer Outcomes',
    what: 'DOJ COPS Office grant for enhancing crisis response training in law enforcement.',
    deadline: 'Application deadline: August 27, 2026',
    eligibility: 'Local, state, tribal, and territorial law-enforcement agencies.',
    link: 'https://cops.usdoj.gov/saferoutcomes',
    total: '~$13M total, 24-month awards',
  },
  {
    name: 'CPD Microgrants',
    what: 'Community Policing Development microgrants for innovative policing programs.',
    deadline: 'Application deadline: August 17, 2026',
    eligibility: 'City, county, and state governments (your municipality applies).',
    link: 'https://cops.usdoj.gov/cpdmicrogrants',
    total: '~$6.7M, ~34 awards',
  },
  {
    name: 'Byrne JAG',
    what: 'Edward Byrne Memorial Justice Assistance Grant — training is an allowable use.',
    deadline: 'Rolling, administered per-state (e.g. in AL: ADECA)',
    eligibility: 'State and local governments via state administering agencies.',
    link: 'https://bja.ojp.gov/program/jag/overview',
    total: 'Formula-based allocation',
  },
] as const;

export function GrantsSection() {
  return (
    <section id="funding" className="py-16 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Your budget isn&apos;t the blocker
          </h2>
          <p className="text-lg text-white/70 max-w-3xl mx-auto">
            Federal and state money exists specifically for de-escalation training.
            CopApp is exactly what these programs fund.
          </p>
        </div>

        {/* Program cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {PROGRAMS.map((p) => (
            <div
              key={p.name}
              className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 flex flex-col"
            >
              <h3 className="text-lg font-bold text-white mb-1">{p.name}</h3>
              <p className="text-sm text-white/70 mb-3">{p.what}</p>
              <div className="space-y-1.5 mb-4 flex-1">
                <p className="text-xs text-blue-400">{p.deadline}</p>
                <p className="text-[10px] text-white/50 italic">
                  Verify current cycle before applying.
                </p>
                <p className="text-xs text-white/60">{p.total}</p>
                <p className="text-xs text-white/60">{p.eligibility}</p>
              </div>
              <a
                href={p.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors mt-auto"
              >
                View program details &rarr;
              </a>
            </div>
          ))}
        </div>

        {/* Insurance + grant help lines */}
        <div className="max-w-3xl mx-auto space-y-4 text-center">
          <p className="text-sm text-white/70">
            <span className="font-medium text-white">Insurance:</span>{' '}
            many municipal risk pools credit documented de-escalation training
            &mdash; we&apos;ll help you make that case to your carrier.
          </p>
          <p className="text-sm text-white/70">
            We&apos;ll help you apply &mdash; request a grant-support packet and
            we&apos;ll respond with program fit and draft language.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 mt-4">
            <a
              href="#request-info"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium transition-all hover:scale-[1.02]"
            >
              Request info / grant help
            </a>
            <Link
              to="/grants"
              className="bg-white/10 hover:bg-white/20 text-white px-8 py-3 rounded-lg font-medium border border-white/20 transition-all hover:scale-[1.02]"
            >
              Grant packet, templates &amp; fact sheet &rarr;
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
